import { useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

interface FoodOrigin { path: string; key: string; y: number }
const origins = new Map<string, FoodOrigin>();
const consumed = new Set<string>();

export function captureFoodOrigin(location: { pathname: string; search: string; key: string }): FoodOrigin {
  const origin = { path: location.pathname + location.search, key: location.key, y: window.scrollY };
  return origin;
}

export function beginFoodFlow(origin: FoodOrigin) {
  origins.set(origin.key, origin);
  consumed.delete(origin.key);
}

export function foodOrigin(state: unknown): FoodOrigin | null {
  const origin = (state as { foodOrigin?: FoodOrigin } | null)?.foodOrigin;
  if (!origin || !/^\/(?:nutrition)?(?:\?|$)/.test(origin.path) || !Number.isFinite(origin.y) || origin.y < 0) return null;
  return origin;
}

/** Wait for asynchronous page content, then restore once. User input cancels the wait. */
export function useFoodScrollRestoration() {
  const location = useLocation();
  const navigation = useNavigationType();
  useLayoutEffect(() => {
    const previous = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    const returned = foodOrigin({ foodOrigin: (location.state as { restoreFoodOrigin?: FoodOrigin } | null)?.restoreFoodOrigin });
    const active = foodOrigin(location.state);
    const path = location.pathname + location.search;
    // History state outlives a flow. Keep only its own food screens or an
    // intentional return; visiting another tab abandons it permanently.
    for (const [key, registered] of origins) {
      const continuing = /^\/nutrition\/(?:food|search|scan)$/.test(location.pathname) && active?.key === key;
      const returning = registered.path === path && (returned?.key === key || (navigation === 'POP' && location.key === key));
      if (!continuing && !returning) origins.delete(key);
    }
    const origin = (returned && origins.get(returned.key)) || (navigation === 'POP' ? origins.get(location.key) : null);
    if (!origin || origin.path !== location.pathname + location.search || consumed.has(location.key)) {
      window.scrollTo(0, 0);
      return () => { history.scrollRestoration = previous; };
    }
    let frame = 0;
    let stopped = false;
    const finish = (consume = true) => {
      stopped = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      if (consume) { origins.delete(origin.key); consumed.add(location.key); }
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchstart', cancel);
      window.removeEventListener('keydown', cancel);
    };
    const cancel = () => finish();
    const attempt = () => {
      if (stopped) return;
      if (document.documentElement.scrollHeight - window.innerHeight >= origin.y) {
        window.scrollTo(0, origin.y);
        finish();
      }
    };
    const observer = new ResizeObserver(() => { cancelAnimationFrame(frame); frame = requestAnimationFrame(attempt); });
    observer.observe(document.body);
    window.addEventListener('wheel', cancel, { passive: true });
    window.addEventListener('touchstart', cancel, { passive: true });
    window.addEventListener('keydown', cancel);
    frame = requestAnimationFrame(attempt);
    return () => { finish(false); history.scrollRestoration = previous; };
  }, [location.key, navigation]);
}
